import {
  forwardRef,
  useId,
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback
} from 'react'

import { useTranslation } from 'react-i18n-lite'

import useIsFocusWithin from '~/hooks/useIsFocusWithin/useIsFocusWithin'
import useIsOpen from '~/hooks/useIsOpen'

import escapeAllRegexSpecialChars from '~/utils/filter/escapeAllRegexSpecialChars'
import normalizeFilterRegex from '~/utils/filter/normalizeFilterRegex'

import Icon from '../../Icon'
import SelectionMenu from '../../Selections/SelectionMenu'

import {
  SelectionMenuOption,
  SelectionMenuProps
} from '../../Selections/SelectionMenu/type'

import Input from '../Input'

import {
  ArrowButton,
  AutocompleteUniselectionContainer,
  IconButton,
  SpinningIcon
} from './styles'

import { AutocompleteUniselectionProps } from './type'

type UseActiveOptionProps = {
  value: string
  isOpen: boolean
  filteredOptions: SelectionMenuProps['options']
}

const useActiveOption = ({
  value,
  isOpen,
  filteredOptions
}: UseActiveOptionProps) => {
  const [activeOptionIndex, setActiveOptionIndex] = useState(-1)

  useEffect(() => {
    if (!isOpen && activeOptionIndex !== -1) {
      setActiveOptionIndex(-1)
    }
  }, [isOpen, activeOptionIndex])

  useEffect(() => {
    setActiveOptionIndex(-1)
  }, [value])

  const setPreviousActiveOption = () =>
    setActiveOptionIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : filteredOptions.length - 1
    )

  const setNextActiveOption = () =>
    setActiveOptionIndex((prevIndex) =>
      prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : 0
    )

  return {
    activeOptionIndex,
    setPreviousActiveOption,
    setNextActiveOption
  }
}

export default forwardRef<HTMLInputElement, AutocompleteUniselectionProps>(
  function AutocompleteUniselection(
    {
      heightClass = 'regular',
      value,
      disabled,
      informational,
      onChange,
      options,
      statusClass,
      ...props
    },
    ref
  ) {
    const selectionMenuId = useId()

    const { isOpen, open, close } = useIsOpen(false)

    const defaultRef = useRef<HTMLInputElement>(null)

    const inputRef = ref || defaultRef

    const focusInput = () => {
      if ('current' in inputRef) {
        inputRef.current?.focus()
      }
    }

    const { t } = useTranslation()

    const [inputValue, setInputValue] = useState<string>(
      typeof value === 'string' ? value : value.des || ''
    )

    const [selectedOption, setSelectedOption] =
      useState<SelectionMenuOption[]>()

    useEffect(() => {
      setInputValue(typeof value === 'string' ? value : value.des || '')
    }, [value])

    const handleInputChange = useCallback(
      (newValue: string) => {
        setInputValue(newValue)
        onChange?.(newValue)
      },
      [setInputValue, onChange]
    )

    const clearValue = () => {
      onChange?.('')

      setInputValue('')

      focusInput()
    }

    const filteredOptions = useMemo(
      () =>
        options.filter((option) =>
          option.value
            .toLowerCase()
            .match(
              normalizeFilterRegex(
                inputValue.toLowerCase(),
                escapeAllRegexSpecialChars
              )
            )
        ),
      [options, inputValue]
    )

    const { activeOptionIndex, setNextActiveOption, setPreviousActiveOption } =
      useActiveOption({
        value: inputValue,
        isOpen,
        filteredOptions
      })

    const onSelect: SelectionMenuProps['onSelect'] = (selected) => {
      const isRemoving = selectedOption?.[0].id === selected.id

      if (isRemoving) {
        clearValue()

        setSelectedOption(undefined)

        return
      }

      focusInput()

      const newValue = selected.uuid
        ? { des: selected.value, uuid: selected.uuid }
        : selected.value

      setSelectedOption([selected])

      onChange?.(newValue)

      setInputValue(selected.value)

      close()
    }

    const onKeyDown: React.KeyboardEventHandler = (event) => {
      if (!isOpen) open()

      switch (event.key) {
        case 'Enter':
          event.preventDefault()

          if (filteredOptions[activeOptionIndex]) {
            onSelect(filteredOptions[activeOptionIndex])
          }

          break
        case 'ArrowDown':
          event.preventDefault()

          setNextActiveOption()

          break
        case 'ArrowUp':
          event.preventDefault()

          setPreviousActiveOption()

          break
        default:
          break
      }
    }

    const activeOptionId = filteredOptions[activeOptionIndex]?.id || undefined

    const { isFocusWithin, containerProps } = useIsFocusWithin()

    const handleClose = useCallback(() => {
      close()

      if (selectedOption) {
        setInputValue((inputValue) => {
          if (selectedOption[0].value !== inputValue) {
            onChange?.(selectedOption[0].value)
          }

          return selectedOption[0].value
        })
      }
    }, [close, selectedOption, setInputValue, onChange])

    useEffect(() => {
      if (!isFocusWithin && isOpen) {
        handleClose()
      }
    }, [isFocusWithin, isOpen, handleClose])

    const toggleList = () => (isOpen ? handleClose() : open())

    const rightAreaElement = () =>
      !informational && (
        <>
          {inputValue &&
            statusClass !== 'processing' &&
            !disabled &&
            isOpen && (
              <IconButton onClick={clearValue} title={t('uniselection.clear')}>
                <Icon iconName='Rubber' icSize='small' />
              </IconButton>
            )}

          {statusClass === 'processing' && <SpinningIcon iconName='Spinner' />}

          <ArrowButton
            isOpen={isOpen}
            disabled={disabled}
            onClick={toggleList}
            title={
              isOpen
                ? t('uniselection.hide-options')
                : t('uniselection.show-options')
            }
          >
            <Icon
              iconName={
                disabled || (!inputValue && options.length === 0)
                  ? 'DropdownArrowDownCircleDashed'
                  : 'DropdownArrowDownCircle'
              }
              preserveFocusOnParent
            />
          </ArrowButton>
        </>
      )

    return (
      <AutocompleteUniselectionContainer {...containerProps}>
        <Input
          {...props}
          onChange={handleInputChange}
          rightAreaElement={rightAreaElement}
          value={inputValue}
          statusClass={statusClass}
          onKeyDown={onKeyDown}
          disabled={disabled}
          informational={informational}
          heightClass={heightClass}
          role='combobox'
          aria-activedescendant={activeOptionId}
          aria-haspopup='listbox'
          aria-controls={selectionMenuId}
          aria-expanded={isOpen}
          aria-busy={statusClass === 'processing'}
          onFocus={open}
          ref={inputRef}
          autoComplete='off'
        />

        {isOpen && !disabled && !informational && (
          <SelectionMenu
            id={selectionMenuId}
            uniselection
            options={filteredOptions}
            selectedOptions={selectedOption}
            activeOptionId={activeOptionId}
            statusClass={statusClass}
            onSelect={onSelect}
          />
        )}
      </AutocompleteUniselectionContainer>
    )
  }
)
